/**
 * Los hooks de datos de TORA. Un hook por operacion.
 *
 * LA INVALIDACION ES LA PARTE SERIA DE ESTE ARCHIVO
 * -------------------------------------------------
 * En una app de contenidos, una cache desincronizada muestra un titulo viejo. En
 * una de plata muestra un numero que no existe: un saldo de cliente que ya se
 * cobro, un stock que ya se vendio, un precio calculado con un dolar de ayer.
 * El vendedor le dice ese numero al cliente en voz alta.
 *
 * Por eso cada mutacion declara TODO lo que su operacion movio, aunque no se vea
 * en la misma pantalla:
 *
 *   · Emitir una venta          -> ventas, stock, productos, precios, clientes,
 *                                  deudas y cajas. Descuenta stock, asienta en
 *                                  cuenta corriente y, si fue al contado, entra
 *                                  plata a la caja.
 *   · Registrar un cobro        -> clientes, deudas, cajas, cheques y ventas.
 *                                  La imputacion es FIFO contra los comprobantes
 *                                  mas viejos, y un pago con cheque suma cartera.
 *   · Cambiar la cotizacion     -> TODO. El trigger `trg_empresa_config_reprecia`
 *                                  recalcula el precio de venta del catalogo
 *                                  entero; cualquier importe cacheado quedo mal.
 *
 * Las claves son jerarquicas (`["stock", "lista", filtros]`) para poder invalidar
 * una familia completa con su prefijo.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import type { Moneda } from "@tora/shared";

import { useDebounce } from "@/hooks/use-debounce";
import { api, ErrorApi } from "@/lib/api";
import type { RangoDeQuery } from "@/lib/periodo";
import type {
  Encargo,
  EstadoEncargo,
  Caja,
  CambiosCliente,
  CambiosConfig,
  CambiosProducto,
  CambiosProveedor,
  Cheque,
  Cliente,
  ComprobanteConDetalle,
  CuentaFinanciera,
  DatosInstalacion,
  CredencialesLogin,
  EstadoInstalacion,
  EstadoOt,
  FichaCliente,
  FilaDeuda,
  FiltrosDeudas,
  FilaReposicion,
  PedidoReposicion,
  FiltrosMovimientos,
  FilaCambioDivisa,
  FilaResultadoNegocio,
  FilaVentaCostoEstimado,
  FilaStock,
  FiltrosClientes,
  FiltrosMovimientosCaja,
  FiltrosTarjetas,
  FiltrosProductos,
  FiltrosReparaciones,
  FiltrosStock,
  StockPorFamilia,
  FiltrosVentas,
  PaginaVentas,
  IngresoStock,
  MovimientoExtracto,
  NuevaOrdenReparacion,
  AccesosEquipo,
  SugerenciasOt,
  UsadoEnTaller,
  CostosOt,
  ResultadoCostosOt,
  SenaOt,
  DevolverSenaOt,
  ResultadoSenaOt,
  EntregaOt,
  ResultadoEntregaOt,
  NuevaNotaCredito,
  ResultadoNotaCredito,
  NuevaTransferencia,
  NuevaVentaContado,
  NuevaVenta,
  NuevoCliente,
  NuevoCobro,
  NuevoMovimientoCaja,
  PaginaMovimientosSocios,
  NuevoProducto,
  OrdenReparacion,
  Pagina,
  Paginacion,
  PagoTecnicoLote,
  PagoTecnicoOt,
  Producto,
  Proveedor,
  SaldoProveedor,
  RespuestaConfig,
  RespuestaPrecios,
  RespuestaSesion,
  ResultadoMovimientoStock,
  ResultadoPagoTecnicoLote,
  ResultadoPagoTecnicoOt,
  ResultadoRecepcion,
  ResultadoPrecio,
  ResultadoVenta,
  ResultadoVentaContado,
  Rubro,
  SalidaStock,
  Socio,
  TableroTaller,
  TotalesCaja,
  Usuario,
  FilaUsuario,
  NuevoUsuario,
  CambiosUsuario,
  PaginaMovimientos,
  PaginaMovimientosCaja,
  PaginaTarjetas,
  NuevoArqueo,
  ResultadoArqueo,
  FrecuenteCaja,
  MovimientoCaja,
  GastoPorCategoria,
} from "@/lib/tipos";

// ─────────────────────────────────────────────────────────────────────────────
// Claves
// ─────────────────────────────────────────────────────────────────────────────

/** Todas las claves de cache en un solo lugar. Sin literales sueltos por ahi. */
export const claves = {
  sesion: ["sesion"] as const,
  instalacion: ["sesion", "estado"] as const,

  precios: ["precios"] as const,
  precio: (q: string, limite: number) => ["precios", q, limite] as const,

  productos: ["productos"] as const,
  listaProductos: (filtros: FiltrosProductos) => ["productos", "lista", filtros] as const,
  producto: (id: number) => ["productos", "uno", id] as const,
  rubros: ["rubros"] as const,
  proveedores: ["proveedores"] as const,

  stock: ["stock"] as const,
  listaStock: (filtros: FiltrosStock) => ["stock", "lista", filtros] as const,
  stockPorFamilia: () => ["stock", "por-familia"] as const,
  alertasStock: (p: Paginacion) => ["stock", "alertas", p] as const,
  reposicion: (p: Paginacion & { proveedor_id?: number }) => ["stock", "reposicion", p] as const,
  movimientosStock: (f: FiltrosMovimientos) => ["stock", "movimientos", f] as const,

  encargos: ["encargos"] as const,
  listaEncargos: (f: { estado?: EstadoEncargo; comprobante_id?: number }) =>
    ["encargos", "lista", f] as const,

  ventas: ["ventas"] as const,
  listaVentas: (filtros: FiltrosVentas) => ["ventas", "lista", filtros] as const,
  venta: (id: number) => ["ventas", "uno", id] as const,

  clientes: ["clientes"] as const,
  listaClientes: (filtros: FiltrosClientes) => ["clientes", "lista", filtros] as const,
  cliente: (id: number) => ["clientes", "uno", id] as const,
  extracto: (id: number, p: Paginacion) => ["clientes", "extracto", id, p] as const,
  deudas: (p: FiltrosDeudas) => ["deudas", p] as const,

  cajas: ["cajas"] as const,
  totalesCaja: ["cajas", "totales"] as const,
  movimientosCaja: (filtros: FiltrosMovimientosCaja) => ["cajas", "movimientos", filtros] as const,
  resultadoNegocio: (r: RangoDeQuery) => ["cajas", "resultado-negocio", r] as const,
  cambios: (r: RangoDeQuery) => ["cajas", "cambios", r] as const,
  costosEstimados: (r: RangoDeQuery) => ["cajas", "costos-estimados", r] as const,
  tarjetas: (f: FiltrosTarjetas) => ["cajas", "tarjetas", f] as const,
  cuentasFinancieras: ["cuentas-financieras"] as const,

  socios: ["socios"] as const,
  movimientosSocios: (r: RangoDeQuery) => ["socios", "movimientos", r] as const,

  cheques: ["cheques"] as const,
  listaCheques: (estado?: string) => ["cheques", "lista", estado ?? "cartera"] as const,

  reparaciones: ["reparaciones"] as const,
  listaReparaciones: (filtros: FiltrosReparaciones) => ["reparaciones", "lista", filtros] as const,
  tableroTaller: ["reparaciones", "tablero"] as const,
  accesosOt: (id: number) => ["reparaciones", "accesos", id] as const,
  sugerenciasOt: ["reparaciones", "sugerencias"] as const,
  usadosEnTaller: ["reparaciones", "en-taller"] as const,

  config: ["config"] as const,
  usuarios: ["usuarios"] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// Invalidacion
// ─────────────────────────────────────────────────────────────────────────────

type Prefijo = readonly unknown[];

/** Invalida varias familias de una. Los `void` son a proposito: no se espera. */
function invalidar(qc: QueryClient, prefijos: Prefijo[]): void {
  for (const queryKey of prefijos) {
    void qc.invalidateQueries({ queryKey: queryKey as unknown[] });
  }
}

/**
 * Lo que toca una venta. Se exporta porque la nota de credito, la anulacion y
 * la facturacion de una orden de taller mueven exactamente lo mismo.
 */
export function invalidarPorComprobante(qc: QueryClient): void {
  invalidar(qc, [
    claves.ventas,
    // El stock bajo: la venta genero salidas.
    claves.stock,
    claves.productos,
    // `buscar_precio` devuelve `disponibilidad` y `stock_actual`: despues de
    // vender el ultimo equipo, la busqueda ya no puede seguir diciendo "hay".
    claves.precios,
    claves.clientes,
    ["deudas"],
    // Una venta al contado entra plata a la caja en el mismo RPC.
    claves.cajas,
  ]);
}

/** Lo que toca un cobro: cuenta corriente, caja y, si hubo cheque, cartera. */
export function invalidarPorCobro(qc: QueryClient): void {
  invalidar(qc, [
    claves.clientes,
    ["deudas"],
    claves.cajas,
    claves.cheques,
    // La imputacion es FIFO: cambia que comprobantes quedaron saldados.
    claves.ventas,
  ]);
}

/** Lo que toca un movimiento de stock. */
export function invalidarPorStock(qc: QueryClient): void {
  invalidar(qc, [claves.stock, claves.productos, claves.precios]);
}

/**
 * La cotizacion cambio: se tira TODA la cache de datos.
 *
 * No es exageracion. `trg_empresa_config_reprecia` recorre el catalogo entero y
 * reescribe `precio_venta` de cada producto. Ademas, las vistas valorizadas
 * (`v_stock_valorizado`, `v_caja_totales`, `v_saldos_clientes` con cuentas
 * ancladas al dolar) calculan con `cotizacion_vigente()` en cada lectura. Todo
 * numero que el frontend tenga guardado quedo viejo en el mismo instante.
 *
 * La sesion se conserva: el usuario no cambio, y refetchearla haria parpadear la
 * app entera contra el login.
 */
export function invalidarPorCotizacion(qc: QueryClient): void {
  void qc.invalidateQueries({
    predicate: (query) => query.queryKey[0] !== "sesion",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Sesion
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Quien esta usando el sistema. `null` significa "nadie": no es un error.
 *
 * El 401 se atrapa aca adentro a proposito. Arrancar sin sesion es el estado
 * normal de la primera visita, y dejar que burbujee como error haria que el
 * ErrorBoundary tape el login que la persona necesita ver.
 */
export function useSesion(): UseQueryResult<Usuario | null, ErrorApi> {
  return useQuery<Usuario | null, ErrorApi>({
    queryKey: claves.sesion,
    queryFn: async ({ signal }) => {
      try {
        const r = await api.get<{ usuario: Usuario }>("/sesion/yo", { signal });
        return r.usuario;
      } catch (error) {
        if (error instanceof ErrorApi && error.noAutenticado) return null;
        throw error;
      }
    },
    // La cookie dura 24 h: no tiene sentido repreguntar quien soy cada 30 s.
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Si el sistema ya tiene usuarios.
 *
 * `false` no manda al login: manda a `/instalar`. Un login en una base vacia es
 * una pared sin puerta — no hay credencial que pueda funcionar.
 */
export function useEstadoInstalacion(): UseQueryResult<EstadoInstalacion, ErrorApi> {
  return useQuery<EstadoInstalacion, ErrorApi>({
    queryKey: claves.instalacion,
    queryFn: ({ signal }) => api.get<EstadoInstalacion>("/sesion/estado", { signal }),
    staleTime: Infinity,
    retry: 1,
  });
}

export function useLogin(): UseMutationResult<RespuestaSesion, ErrorApi, CredencialesLogin> {
  const qc = useQueryClient();
  return useMutation<RespuestaSesion, ErrorApi, CredencialesLogin>({
    mutationFn: (credenciales) => api.post<RespuestaSesion>("/sesion/login", credenciales),
    onSuccess: (r) => {
      qc.setQueryData(claves.sesion, r.usuario);
      // Entra alguien nuevo: lo que quedo en cache es de la sesion anterior y
      // puede ser de otro usuario con otros permisos.
      void qc.invalidateQueries();
    },
  });
}

export function useLogout(): UseMutationResult<{ ok: boolean }, ErrorApi, void> {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, ErrorApi, void>({
    mutationFn: () => api.post<{ ok: boolean }>("/sesion/logout"),
    // `onSettled` y no `onSuccess`: si el logout falla igual hay que borrar los
    // datos del navegador. Dejar el saldo del cliente en pantalla despues de que
    // alguien apreto "salir" es peor que un error de red.
    onSettled: () => {
      qc.setQueryData(claves.sesion, null);
      qc.clear();
    },
  });
}

/** Alta del primer usuario. Solo funciona con la base sin usuarios. */
export function useInstalar(): UseMutationResult<RespuestaSesion, ErrorApi, DatosInstalacion> {
  const qc = useQueryClient();
  return useMutation<RespuestaSesion, ErrorApi, DatosInstalacion>({
    mutationFn: (datos) => api.post<RespuestaSesion>("/sesion/instalar", datos),
    onSuccess: (r) => {
      // El backend ya dejo la cookie puesta: se entra derecho, sin pedir el
      // login que la persona acaba de definir.
      qc.setQueryData(claves.sesion, r.usuario);
      qc.setQueryData(claves.instalacion, { instalado: true });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Precios — la consulta mas frecuente del dia
// ─────────────────────────────────────────────────────────────────────────────

export interface BusquedaPrecio {
  resultados: ResultadoPrecio[];
  total: number;
  /** Hay una consulta en curso o el debounce todavia no vencio. */
  buscando: boolean;
  /** Se busco algo y no hay nada. Distinto de "todavia no busco nada". */
  sinResultados: boolean;
  error: ErrorApi | null;
  consulta: UseQueryResult<RespuestaPrecios, ErrorApi>;
}

/**
 * "Cuanto sale y si hay". Es lo que mas se usa de todo el sistema.
 *
 * Dos decisiones de comportamiento, las dos por lo mismo: el cliente esta
 * esperando del otro lado del WhatsApp.
 *
 *   · debounce de 250 ms — una consulta por palabra escrita, no por tecla.
 *   · `keepPreviousData`  — mientras llega la consulta nueva se siguen viendo
 *     los resultados anteriores. Sin esto la lista se vacia en cada letra y la
 *     pantalla parpadea; con el precio a la vista, ese parpadeo hace que alguien
 *     lea el numero equivocado.
 */
export function useBuscarPrecio(texto: string, limite = 10): BusquedaPrecio {
  const demorado = useDebounce(texto, 250);
  const q = demorado.trim();
  const habilitada = q.length > 0;

  const consulta = useQuery<RespuestaPrecios, ErrorApi>({
    queryKey: claves.precio(q, limite),
    queryFn: ({ signal }) =>
      api.get<RespuestaPrecios>("/precios", { params: { q, limite }, signal }),
    enabled: habilitada,
    placeholderData: keepPreviousData,
    // Los precios se mueven cuando se mueve el dolar, no cada 30 segundos.
    staleTime: 60_000,
  });

  return {
    resultados: consulta.data?.resultados ?? [],
    total: consulta.data?.total ?? 0,
    // El debounce pendiente cuenta como "buscando": si no, entre la tecla y la
    // consulta la UI dice "sin resultados" durante 250 ms.
    buscando: habilitada && (consulta.isFetching || texto.trim() !== q),
    sinResultados:
      habilitada && !consulta.isFetching && texto.trim() === q && (consulta.data?.total ?? 0) === 0,
    error: consulta.error ?? null,
    consulta,
  };
}

/** Version con el input adentro, para pantallas que solo quieren el buscador. */
export function useBuscadorPrecios(limite = 10): BusquedaPrecio & {
  texto: string;
  setTexto: (v: string) => void;
} {
  const [texto, setTexto] = useState("");
  return { ...useBuscarPrecio(texto, limite), texto, setTexto };
}

// ─────────────────────────────────────────────────────────────────────────────
// Productos
// ─────────────────────────────────────────────────────────────────────────────

export function useProductos(
  filtros: FiltrosProductos = {},
): UseQueryResult<Pagina<Producto>, ErrorApi> {
  return useQuery<Pagina<Producto>, ErrorApi>({
    queryKey: claves.listaProductos(filtros),
    queryFn: ({ signal }) =>
      api.get<Pagina<Producto>>("/productos", { params: { ...filtros }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useProducto(id: number | undefined): UseQueryResult<Producto, ErrorApi> {
  return useQuery<Producto, ErrorApi>({
    queryKey: claves.producto(id ?? 0),
    queryFn: ({ signal }) => api.get<Producto>(`/productos/${id}`, { signal }),
    enabled: typeof id === "number" && id > 0,
  });
}

export function useCrearProducto(): UseMutationResult<Producto, ErrorApi, NuevoProducto> {
  const qc = useQueryClient();
  return useMutation<Producto, ErrorApi, NuevoProducto>({
    mutationFn: (datos) => api.post<Producto>("/productos", datos),
    onSuccess: (producto) => {
      qc.setQueryData(claves.producto(producto.id), producto);
      invalidar(qc, [claves.productos, claves.stock, claves.precios]);
    },
  });
}

export function useActualizarProducto(): UseMutationResult<
  Producto,
  ErrorApi,
  { id: number; cambios: CambiosProducto }
> {
  const qc = useQueryClient();
  return useMutation<Producto, ErrorApi, { id: number; cambios: CambiosProducto }>({
    mutationFn: ({ id, cambios }) => api.patch<Producto>(`/productos/${id}`, cambios),
    onSuccess: (producto) => {
      qc.setQueryData(claves.producto(producto.id), producto);
      // Tocar costo, markup o redondeo recalcula `precio_venta`: la busqueda de
      // precios y el stock valorizado quedaron viejos.
      invalidar(qc, [claves.productos, claves.stock, claves.precios]);
    },
  });
}

export function useRubros(): UseQueryResult<Pagina<Rubro>, ErrorApi> {
  return useQuery<Pagina<Rubro>, ErrorApi>({
    queryKey: claves.rubros,
    queryFn: ({ signal }) => api.get<Pagina<Rubro>>("/rubros", { signal }),
    staleTime: 10 * 60_000,
  });
}

/**
 * `incluirBajas` solo lo usa la pantalla de proveedores, que tiene que poder
 * ver un proveedor dado de baja para reactivarlo. Los desplegables del resto
 * del sistema piden la lista sin parametro y siguen viendo solo los activos:
 * ofrecer para elegir a uno que esta de baja es como no haberlo dado de baja.
 */
/**
 * Los proveedores, con su saldo adentro.
 *
 * Desde 0062 el endpoint devuelve `v_saldos_proveedores`, que es un superset de
 * la tabla: los desplegables siguen leyendo `nombre` y `moneda_lista` como
 * siempre, y la pantalla de proveedores encuentra ademas cuanto se le debe a
 * cada uno sin pedirlo aparte.
 */
export function useProveedores(
  incluirBajas = false,
): UseQueryResult<Pagina<SaldoProveedor>, ErrorApi> {
  return useQuery<Pagina<SaldoProveedor>, ErrorApi>({
    queryKey: [...claves.proveedores, incluirBajas] as const,
    queryFn: ({ signal }) =>
      api.get<Pagina<SaldoProveedor>>("/proveedores", {
        params: incluirBajas ? { activo: "todos" } : {},
        signal,
      }),
    staleTime: 10 * 60_000,
  });
}

export function useActualizarProveedor(): UseMutationResult<
  Proveedor,
  ErrorApi,
  { id: number; cambios: CambiosProveedor }
> {
  const qc = useQueryClient();
  return useMutation<Proveedor, ErrorApi, { id: number; cambios: CambiosProveedor }>({
    mutationFn: ({ id, cambios }) => api.patch<Proveedor>(`/proveedores/${id}`, cambios),
    // La moneda de lista del proveedor es el default de la carga de compras y
    // aparece en la ficha del producto, asi que no alcanza con refrescar la
    // lista de proveedores.
    onSuccess: () => invalidar(qc, [claves.proveedores, claves.productos]),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Stock
// ─────────────────────────────────────────────────────────────────────────────

export function useStock(filtros: FiltrosStock = {}): UseQueryResult<Pagina<FilaStock>, ErrorApi> {
  return useQuery<Pagina<FilaStock>, ErrorApi>({
    queryKey: claves.listaStock(filtros),
    queryFn: ({ signal }) =>
      api.get<Pagina<FilaStock>>("/stock", { params: { ...filtros }, signal }),
    placeholderData: keepPreviousData,
  });
}

/** El inventario sumado por familia. Sin paginar: son tantas filas como familias. */
export function useStockPorFamilia(): UseQueryResult<Pagina<StockPorFamilia>, ErrorApi> {
  return useQuery<Pagina<StockPorFamilia>, ErrorApi>({
    queryKey: claves.stockPorFamilia(),
    queryFn: ({ signal }) => api.get<Pagina<StockPorFamilia>>("/stock/por-familia", { signal }),
  });
}

export function useAlertasStock(p: Paginacion = {}): UseQueryResult<Pagina<FilaStock>, ErrorApi> {
  return useQuery<Pagina<FilaStock>, ErrorApi>({
    queryKey: claves.alertasStock(p),
    queryFn: ({ signal }) =>
      api.get<Pagina<FilaStock>>("/stock/alertas", { params: { ...p }, signal }),
  });
}

/** Un aviso de la campanita: algo mal cargado o que falta cargar (`GET /avisos`). */
export interface Aviso {
  clave: string;
  tipo: "ERROR" | "AVISO" | "FALTA";
  titulo: string;
  detalle: string;
  cantidad: number;
  casos: Array<{ texto: string; ruta?: string }>;
}

/**
 * Los avisos de la campanita. Se refrescan cada dos minutos y al volver a la
 * ventana: son la cuenta de lo que falta arreglar, y arreglar algo tiene que
 * bajarla sin recargar la pagina.
 */
export function useAvisos(): UseQueryResult<{ avisos: Aviso[]; total: number }, ErrorApi> {
  return useQuery<{ avisos: Aviso[]; total: number }, ErrorApi>({
    queryKey: ["avisos"],
    queryFn: ({ signal }) => api.get<{ avisos: Aviso[]; total: number }>("/avisos", { signal }),
    refetchInterval: 120_000,
  });
}

export function useReposicion(
  p: Paginacion & { proveedor_id?: number } = {},
): UseQueryResult<Pagina<FilaReposicion>, ErrorApi> {
  return useQuery<Pagina<FilaReposicion>, ErrorApi>({
    queryKey: claves.reposicion(p),
    queryFn: ({ signal }) =>
      api.get<Pagina<FilaReposicion>>("/stock/reposicion", { params: { ...p }, signal }),
  });
}

/**
 * Anota lo que se le pidio a un proveedor (0102). Lo dispara el boton de
 * WhatsApp solo: no es un paso aparte, es el mismo clic.
 */
export function useAnotarPedidoReposicion(): UseMutationResult<
  { anotados: number },
  ErrorApi,
  PedidoReposicion
> {
  const qc = useQueryClient();
  return useMutation<{ anotados: number }, ErrorApi, PedidoReposicion>({
    mutationFn: (datos) => api.post<{ anotados: number }>("/stock/reposicion/pedido", datos),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stock", "reposicion"] }),
  });
}

/** Deshace el pedido abierto de un producto: vuelve a sugerirse. */
export function useAnularPedidoReposicion(): UseMutationResult<
  { anulados: number },
  ErrorApi,
  number
> {
  const qc = useQueryClient();
  return useMutation<{ anulados: number }, ErrorApi, number>({
    mutationFn: (productoId) =>
      api.delete<{ anulados: number }>(`/stock/reposicion/pedido/${productoId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stock", "reposicion"] }),
  });
}

/**
 * La linea de tiempo del stock. Cuelga de `claves.stock`, asi que cualquier
 * ingreso, salida o conteo la refresca solo via `invalidarPorStock`.
 */
export function useMovimientosStock(
  filtros: FiltrosMovimientos,
): UseQueryResult<PaginaMovimientos, ErrorApi> {
  return useQuery<PaginaMovimientos, ErrorApi>({
    queryKey: claves.movimientosStock(filtros),
    queryFn: ({ signal }) =>
      api.get<PaginaMovimientos>("/stock/movimientos", { params: { ...filtros }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useIngresoStock(): UseMutationResult<
  ResultadoMovimientoStock,
  ErrorApi,
  IngresoStock
> {
  const qc = useQueryClient();
  return useMutation<ResultadoMovimientoStock, ErrorApi, IngresoStock>({
    mutationFn: (datos) => api.post<ResultadoMovimientoStock>("/stock/ingreso", datos),
    // Un ingreso con `actualiza_costo` (el default) reescribe el costo de
    // reposicion y con eso el precio de venta: por eso tambien `precios`.
    onSuccess: () => invalidarPorStock(qc),
  });
}

export function useSalidaStock(): UseMutationResult<
  ResultadoMovimientoStock,
  ErrorApi,
  SalidaStock
> {
  const qc = useQueryClient();
  return useMutation<ResultadoMovimientoStock, ErrorApi, SalidaStock>({
    mutationFn: (datos) => api.post<ResultadoMovimientoStock>("/stock/salida", datos),
    onSuccess: (_r, datos) => {
      invalidarPorStock(qc);
      if (datos.tipo === "RETIRO_SOCIO") invalidar(qc, [claves.socios]);
    },
  });
}

/**
 * Borrado real. El backend lo rechaza si el producto tiene ventas registradas
 * (el mensaje llega en el error) y ahí el camino es desactivar.
 */
export function useEliminarProducto(): UseMutationResult<
  { ok: boolean; producto_id: number },
  ErrorApi,
  number
> {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; producto_id: number }, ErrorApi, number>({
    mutationFn: (id) => api.delete<{ ok: boolean; producto_id: number }>(`/productos/${id}`),
    onSuccess: () => {
      invalidarPorStock(qc);
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Ventas
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `PaginaVentas` y no `Pagina<Comprobante>`: la respuesta trae ademas los
 * totales por moneda del set filtrado COMPLETO. El pie de la lista los usa tal
 * cual en vez de sumar las filas que llegaron, que son una pagina.
 */
export function useVentas(filtros: FiltrosVentas = {}): UseQueryResult<PaginaVentas, ErrorApi> {
  return useQuery<PaginaVentas, ErrorApi>({
    queryKey: claves.listaVentas(filtros),
    queryFn: ({ signal }) => api.get<PaginaVentas>("/ventas", { params: { ...filtros }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useVenta(id: number | undefined): UseQueryResult<ComprobanteConDetalle, ErrorApi> {
  return useQuery<ComprobanteConDetalle, ErrorApi>({
    queryKey: claves.venta(id ?? 0),
    queryFn: ({ signal }) => api.get<ComprobanteConDetalle>(`/ventas/${id}`, { signal }),
    enabled: typeof id === "number" && id > 0,
  });
}

/**
 * Emitir una venta.
 *
 * El error de credito excedido NO se atrapa aca: sube con su texto entero
 * ("La venta excede el credito disponible del cliente (queda $ 500.000).
 * Confirma la venta con forzar si igual queres hacerla"). La pantalla lo muestra
 * y ofrece reintentar con `forzar: true`. Traducirlo a "no se pudo" le sacaria
 * al vendedor la unica informacion que necesita para decidir.
 */
export function useEmitirVenta(): UseMutationResult<ResultadoVenta, ErrorApi, NuevaVenta> {
  const qc = useQueryClient();
  return useMutation<ResultadoVenta, ErrorApi, NuevaVenta>({
    mutationFn: (venta) => api.post<ResultadoVenta>("/ventas", venta),
    onSuccess: () => invalidarPorComprobante(qc),
  });
}

/**
 * La venta de mostrador: alguien entra, se lleva el equipo, paga y se va.
 *
 * Es UNA sola llamada y una sola transaccion en la base (`emitir_venta_contado`):
 * emite el comprobante, descuenta el stock, mete la plata en las cajas que el
 * vendedor eligio y deja el comprobante saldado. Hacerlo en dos pasos desde el
 * frontend —`/ventas` y despues `/cobros`— abre la ventana en la que la venta
 * quedo emitida y la plata no entro a ningun lado, que es justo el agujero que
 * aparece cuando se corta la luz en el medio.
 *
 * La respuesta trae `diferencia`: positiva es lo que falta cobrar, negativa es el
 * vuelto. La pantalla la muestra en el momento en que se cuentan los billetes.
 *
 * Invalida lo mismo que cualquier venta. La caja entra por `claves.cajas`, que
 * es prefijo de los saldos, los totales y los movimientos.
 */
export function useEmitirVentaContado(): UseMutationResult<
  ResultadoVentaContado,
  ErrorApi,
  NuevaVentaContado
> {
  const qc = useQueryClient();
  return useMutation<ResultadoVentaContado, ErrorApi, NuevaVentaContado>({
    mutationFn: (venta) => api.post<ResultadoVentaContado>("/ventas/contado", venta),
    onSuccess: () => invalidarPorComprobante(qc),
  });
}

export function useNotaCredito(): UseMutationResult<
  ResultadoNotaCredito,
  ErrorApi,
  { id: number; datos?: NuevaNotaCredito }
> {
  const qc = useQueryClient();
  return useMutation<ResultadoNotaCredito, ErrorApi, { id: number; datos?: NuevaNotaCredito }>({
    mutationFn: ({ id, datos }) =>
      api.post<ResultadoNotaCredito>(`/ventas/${id}/nota-credito`, datos ?? {}),
    onSuccess: (_r, { id }) => {
      // La venta original cambia de saldo imputado: se refresca su ficha.
      void qc.invalidateQueries({ queryKey: claves.venta(id) });
      invalidarPorComprobante(qc);
    },
  });
}

/** Anular es de admin. La base frena si el comprobante ya tiene cobros imputados. */
export function useAnularComprobante(): UseMutationResult<
  unknown,
  ErrorApi,
  { id: number; motivo: string }
> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, { id: number; motivo: string }>({
    mutationFn: ({ id, motivo }) => api.post(`/ventas/${id}/anular`, { motivo }),
    onSuccess: (_r, { id }) => {
      void qc.invalidateQueries({ queryKey: claves.venta(id) });
      invalidarPorComprobante(qc);
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Clientes y cuenta corriente
// ─────────────────────────────────────────────────────────────────────────────

export function useClientes(
  filtros: FiltrosClientes = {},
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<Pagina<Cliente>, ErrorApi> {
  return useQuery<Pagina<Cliente>, ErrorApi>({
    queryKey: claves.listaClientes(filtros),
    queryFn: ({ signal }) =>
      api.get<Pagina<Cliente>>("/clientes", { params: { ...filtros }, signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** Ficha, saldo y mora en una sola llamada: las tres se miran juntas siempre. */
export function useCliente(id: number | undefined): UseQueryResult<FichaCliente, ErrorApi> {
  return useQuery<FichaCliente, ErrorApi>({
    queryKey: claves.cliente(id ?? 0),
    queryFn: ({ signal }) => api.get<FichaCliente>(`/clientes/${id}`, { signal }),
    enabled: typeof id === "number" && id > 0,
  });
}

/**
 * El libro mayor del cliente.
 *
 * Viene en orden ascendente y asi hay que mostrarlo: `saldo_acumulado` solo
 * tiene sentido leido de arriba hacia abajo.
 */
export function useExtracto(
  id: number | undefined,
  p: Paginacion = {},
): UseQueryResult<Pagina<MovimientoExtracto>, ErrorApi> {
  return useQuery<Pagina<MovimientoExtracto>, ErrorApi>({
    queryKey: claves.extracto(id ?? 0, p),
    queryFn: ({ signal }) =>
      api.get<Pagina<MovimientoExtracto>>(`/clientes/${id}/extracto`, { params: { ...p }, signal }),
    enabled: typeof id === "number" && id > 0,
  });
}

export function useCrearCliente(): UseMutationResult<Cliente, ErrorApi, NuevoCliente> {
  const qc = useQueryClient();
  return useMutation<Cliente, ErrorApi, NuevoCliente>({
    mutationFn: (datos) => api.post<Cliente>("/clientes", datos),
    onSuccess: () => invalidar(qc, [claves.clientes]),
  });
}

export function useActualizarCliente(): UseMutationResult<
  Cliente,
  ErrorApi,
  { id: number; cambios: CambiosCliente }
> {
  const qc = useQueryClient();
  return useMutation<Cliente, ErrorApi, { id: number; cambios: CambiosCliente }>({
    mutationFn: ({ id, cambios }) => api.patch<Cliente>(`/clientes/${id}`, cambios),
    onSuccess: (_c, { id }) => {
      void qc.invalidateQueries({ queryKey: claves.cliente(id) });
      // Cambiar el limite de credito cambia quien aparece excedido en deudas.
      invalidar(qc, [claves.clientes, ["deudas"]]);
    },
  });
}

/**
 * Quien me debe y hace cuanto. Ordenado por la edad del comprobante mas viejo.
 *
 * Sin filtros trae SOLO mora y excedidos (es el contador del header). Para
 * cobrar hace falta `con_saldo: "1"`: una venta a cuenta de hoy esta al dia y
 * no entra en el recorte de alertas.
 */
export function useDeudas(
  p: FiltrosDeudas = {},
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<Pagina<FilaDeuda>, ErrorApi> {
  return useQuery<Pagina<FilaDeuda>, ErrorApi>({
    queryKey: claves.deudas(p),
    queryFn: ({ signal }) => api.get<Pagina<FilaDeuda>>("/deudas", { params: { ...p }, signal }),
    enabled,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Cobros
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cobrar con uno o varios medios de pago.
 *
 * Un cobro real es "me dio 200 mil en efectivo, 300 mil por transferencia y un
 * cheque a 30 dias". Mueve la caja, la cuenta corriente y —si hubo cheque— la
 * cartera, todo en una transaccion.
 */
export function useRegistrarCobro(): UseMutationResult<unknown, ErrorApi, NuevoCobro> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, NuevoCobro>({
    mutationFn: (cobro) => api.post("/cobros", cobro),
    onSuccess: (_r, cobro) => {
      void qc.invalidateQueries({ queryKey: claves.cliente(cobro.cliente_id) });
      invalidarPorCobro(qc);
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Caja
// ─────────────────────────────────────────────────────────────────────────────

/** Los saldos de todas las cajas. Es lo primero que se mira al abrir el dia. */
export function useCajas(): UseQueryResult<Caja[], ErrorApi> {
  return useQuery<Caja[], ErrorApi>({
    queryKey: claves.cajas,
    queryFn: async ({ signal }) => {
      const r = await api.get<{ cajas: Caja[] }>("/cajas", { signal });
      return r.cajas;
    },
  });
}

/** Totales por moneda, con la cotizacion que se uso para consolidarlos. */
export function useTotalesCaja(): UseQueryResult<TotalesCaja | null, ErrorApi> {
  return useQuery<TotalesCaja | null, ErrorApi>({
    queryKey: claves.totalesCaja,
    queryFn: ({ signal }) => api.get<TotalesCaja | null>("/cajas/totales", { signal }),
  });
}

/**
 * Los movimientos del periodo, con el pie que los resume.
 *
 * `totales` viene del servidor sobre el set filtrado COMPLETO: sumar en el
 * cliente lo que trajo la pagina da un total parcial sin avisar.
 */
export function useMovimientosCaja(
  filtros: FiltrosMovimientosCaja = {},
): UseQueryResult<PaginaMovimientosCaja, ErrorApi> {
  return useQuery<PaginaMovimientosCaja, ErrorApi>({
    queryKey: claves.movimientosCaja(filtros),
    queryFn: ({ signal }) =>
      api.get<PaginaMovimientosCaja>("/cajas/movimientos", { params: { ...filtros }, signal }),
    placeholderData: keepPreviousData,
  });
}

/** Las dos ganancias por dia: margen operativo y resultado financiero. */
export function useResultadoNegocio(
  rango: RangoDeQuery = {},
): UseQueryResult<Pagina<FilaResultadoNegocio>, ErrorApi> {
  return useQuery<Pagina<FilaResultadoNegocio>, ErrorApi>({
    queryKey: claves.resultadoNegocio(rango),
    queryFn: ({ signal }) =>
      api.get<Pagina<FilaResultadoNegocio>>("/cajas/resultado-negocio", {
        params: { ...rango },
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/**
 * Lo vendido sin stock que espera la compra: su ganancia es estimada (0106).
 * El rango solo recorta lo vendido con costo 0 (0121_3): lo estimado es lo que
 * falta hoy y viene entero mire el periodo que mire.
 */
export function useCostosEstimados(
  rango: RangoDeQuery = {},
): UseQueryResult<Pagina<FilaVentaCostoEstimado>, ErrorApi> {
  return useQuery<Pagina<FilaVentaCostoEstimado>, ErrorApi>({
    queryKey: claves.costosEstimados(rango),
    queryFn: ({ signal }) =>
      api.get<Pagina<FilaVentaCostoEstimado>>("/cajas/costos-estimados", {
        params: { ...rango },
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/** Cada compra o venta de dolares, con lo que dejo contra la cotizacion del dia. */
export function useCambiosDivisa(
  rango: RangoDeQuery = {},
): UseQueryResult<Pagina<FilaCambioDivisa>, ErrorApi> {
  return useQuery<Pagina<FilaCambioDivisa>, ErrorApi>({
    queryKey: claves.cambios(rango),
    queryFn: ({ signal }) =>
      api.get<Pagina<FilaCambioDivisa>>("/cajas/cambios", { params: { ...rango }, signal }),
    placeholderData: keepPreviousData,
  });
}

/**
 * Lo cobrado con tarjeta. Dos usos:
 *   · `{ acreditado: false }` sin periodo -> la plata que todavia no esta, que
 *     es un saldo y se muestra en Caja.
 *   · con periodo -> cuanto cobro la tarjeta ese mes, en Ganancias.
 */
export function useTarjetas(
  filtros: FiltrosTarjetas = {},
): UseQueryResult<PaginaTarjetas, ErrorApi> {
  return useQuery<PaginaTarjetas, ErrorApi>({
    queryKey: claves.tarjetas(filtros),
    queryFn: ({ signal }) =>
      api.get<PaginaTarjetas>("/cajas/tarjetas", {
        params: {
          ...(filtros.desde ? { desde: filtros.desde } : {}),
          ...(filtros.hasta ? { hasta: filtros.hasta } : {}),
          ...(filtros.acreditado === undefined ? {} : { acreditado: String(filtros.acreditado) }),
        },
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useCuentasFinancieras(): UseQueryResult<Pagina<CuentaFinanciera>, ErrorApi> {
  return useQuery<Pagina<CuentaFinanciera>, ErrorApi>({
    queryKey: claves.cuentasFinancieras,
    queryFn: ({ signal }) => api.get<Pagina<CuentaFinanciera>>("/cuentas-financieras", { signal }),
    staleTime: 10 * 60_000,
  });
}

/** Un gasto, un retiro, un aporte. La moneda la pone la cuenta, no el formulario. */
export function useRegistrarMovimiento(): UseMutationResult<
  unknown,
  ErrorApi,
  NuevoMovimientoCaja
> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, NuevoMovimientoCaja>({
    mutationFn: (mov) => api.post("/cajas/movimientos", mov),
    // Un pago a proveedor cargado desde la caja baja su saldo, y un retiro o
    // un aporte cambia la cuenta de un socio.
    onSuccess: () => invalidar(qc, [claves.cajas, claves.proveedores, claves.socios]),
  });
}

/** Transferencia entre cajas. Si las monedas difieren, es un cambio de divisa. */
export function useTransferencia(): UseMutationResult<unknown, ErrorApi, NuevaTransferencia> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, NuevaTransferencia>({
    mutationFn: (datos) => api.post("/cajas/transferencias", datos),
    onSuccess: () => invalidar(qc, [claves.cajas]),
  });
}

/**
 * Arqueo: se conto la plata de una caja.
 *
 * NO manda el esperado y nunca lo pide antes: lo calcula la base. Un arqueo
 * donde el numero a alcanzar esta a la vista no controla nada (ver 0069 y
 * `DialogArqueo`).
 */
export function useArqueo(): UseMutationResult<ResultadoArqueo, ErrorApi, NuevoArqueo> {
  const qc = useQueryClient();
  return useMutation<ResultadoArqueo, ErrorApi, NuevoArqueo>({
    mutationFn: (datos) => api.post<ResultadoArqueo>("/cajas/arqueo", datos),
    // Se invalida la caja entera: el ajuste de la diferencia es un movimiento
    // mas, y los saldos de arriba cambian.
    onSuccess: () => invalidar(qc, [claves.cajas]),
  });
}

/**
 * Cerrar la caja: los cajones contados en un solo paso (0155). Cada conteo es
 * un arqueo, y van todos juntos o ninguno.
 */
export function useCierreCaja(): UseMutationResult<
  { arqueos: ResultadoArqueo[] },
  ErrorApi,
  { conteos: Array<{ cuenta_id: number; contado: number }>; observaciones?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (datos) => api.post<{ arqueos: ResultadoArqueo[] }>("/cajas/cierre", datos),
    onSuccess: () => invalidar(qc, [claves.cajas]),
  });
}

/**
 * Anular un movimiento cargado a mano (0155): la base asienta el espejo, y
 * Ganancias y Socios dejan afuera a los dos.
 */
export function useAnularMovimiento(): UseMutationResult<
  unknown,
  ErrorApi,
  { id: number; motivo?: string }
> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, { id: number; motivo?: string }>({
    mutationFn: ({ id, motivo }) =>
      api.post(`/cajas/movimientos/${id}/anular`, motivo ? { motivo } : {}),
    onSuccess: () => invalidar(qc, [claves.cajas, claves.socios]),
  });
}

/** Lo que mas se carga a mano (0156): propone la categoria y arma los frecuentes. */
export function useFrecuentesCaja(): UseQueryResult<{ filas: FrecuenteCaja[] }, ErrorApi> {
  return useQuery<{ filas: FrecuenteCaja[] }, ErrorApi>({
    queryKey: [...claves.cajas, "frecuentes"],
    queryFn: ({ signal }) => api.get<{ filas: FrecuenteCaja[] }>("/cajas/frecuentes", { signal }),
    staleTime: 5 * 60_000,
  });
}

/** Lo que cargo otra persona y falta revisar (0156). */
export function useParaRevisar(): UseQueryResult<Pagina<MovimientoCaja>, ErrorApi> {
  return useQuery<Pagina<MovimientoCaja>, ErrorApi>({
    queryKey: [...claves.cajas, "para-revisar"],
    queryFn: ({ signal }) => api.get<Pagina<MovimientoCaja>>("/cajas/para-revisar", { signal }),
  });
}

/** «Está bien así» o «Cambiar categoría» (0156). */
export function useRevisarMovimiento(): UseMutationResult<
  unknown,
  ErrorApi,
  { id: number; concepto?: string; socio_id?: number; nota?: string }
> {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ErrorApi,
    { id: number; concepto?: string; socio_id?: number; nota?: string }
  >({
    mutationFn: ({ id, ...datos }) => api.post(`/cajas/movimientos/${id}/revisar`, datos),
    onSuccess: () => invalidar(qc, [claves.cajas, claves.socios, ["avisos"]]),
  });
}

/** Los gastos del periodo, por categoria (0156). */
export function useGastosPorCategoria(rango: {
  desde?: string;
  hasta?: string;
}): UseQueryResult<{ filas: GastoPorCategoria[] }, ErrorApi> {
  return useQuery<{ filas: GastoPorCategoria[] }, ErrorApi>({
    queryKey: [...claves.cajas, "gastos-por-categoria", rango],
    queryFn: ({ signal }) =>
      api.get<{ filas: GastoPorCategoria[] }>("/cajas/gastos-por-categoria", {
        params: rango,
        signal,
      }),
  });
}

/** El sueldo pactado de un socio (0156). Sin importe, se borra. */
export function useFijarSueldoSocio(): UseMutationResult<
  unknown,
  ErrorApi,
  { id: number; sueldo_importe: number | null; sueldo_moneda: "ARS" | "USD" | null }
> {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ErrorApi,
    { id: number; sueldo_importe: number | null; sueldo_moneda: "ARS" | "USD" | null }
  >({
    mutationFn: ({ id, ...datos }) => api.patch(`/socios/${id}/sueldo`, datos),
    onSuccess: () => invalidar(qc, [claves.socios]),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Cheques
// ─────────────────────────────────────────────────────────────────────────────

/** Sin `estado` trae la cartera (`v_cheques_cartera`); con estado, la tabla cruda. */
export function useCheques(estado?: string): UseQueryResult<Pagina<Cheque>, ErrorApi> {
  return useQuery<Pagina<Cheque>, ErrorApi>({
    queryKey: claves.listaCheques(estado),
    queryFn: ({ signal }) => api.get<Pagina<Cheque>>("/cheques", { params: { estado }, signal }),
  });
}

export function useDepositarCheque(): UseMutationResult<
  unknown,
  ErrorApi,
  { id: number; cuenta_id: number; fecha?: string }
> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, { id: number; cuenta_id: number; fecha?: string }>({
    mutationFn: ({ id, ...datos }) => api.post(`/cheques/${id}/depositar`, datos),
    // Depositar mueve plata de la cartera a una cuenta: cambia el saldo de caja.
    onSuccess: () => invalidar(qc, [claves.cheques, claves.cajas]),
  });
}

/** Endosar un cheque a un proveedor (0155): sale de la cuenta y baja la deuda. */
export function useEndosarCheque(): UseMutationResult<
  unknown,
  ErrorApi,
  { id: number; proveedor_id: number; moneda_deuda?: "ARS" | "USD"; cotizacion?: number }
> {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ErrorApi,
    { id: number; proveedor_id: number; moneda_deuda?: "ARS" | "USD"; cotizacion?: number }
  >({
    mutationFn: ({ id, ...datos }) => api.post(`/cheques/${id}/endosar`, datos),
    onSuccess: () => invalidar(qc, [claves.cheques, claves.cajas, claves.proveedores]),
  });
}

/** «Se acreditó» (0155): el cheque depositado pasa a cobrado. No mueve plata. */
export function useAcreditarCheque(): UseMutationResult<unknown, ErrorApi, { id: number }> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, { id: number }>({
    mutationFn: ({ id }) => api.post(`/cheques/${id}/acreditar`, {}),
    onSuccess: () => invalidar(qc, [claves.cheques, claves.cajas]),
  });
}

export function useRechazarCheque(): UseMutationResult<
  unknown,
  ErrorApi,
  { id: number; motivo?: string }
> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, { id: number; motivo?: string }>({
    mutationFn: ({ id, motivo }) => api.post(`/cheques/${id}/rechazar`, { motivo }),
    // Un rechazo revierte el cobro: la deuda del cliente vuelve a existir.
    onSuccess: () => invalidar(qc, [claves.cheques, claves.cajas, claves.clientes, ["deudas"]]),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Taller
// ─────────────────────────────────────────────────────────────────────────────

export function useReparaciones(
  filtros: FiltrosReparaciones = {},
): UseQueryResult<Pagina<OrdenReparacion>, ErrorApi> {
  return useQuery<Pagina<OrdenReparacion>, ErrorApi>({
    queryKey: claves.listaReparaciones(filtros),
    queryFn: ({ signal }) =>
      api.get<Pagina<OrdenReparacion>>("/reparaciones", { params: { ...filtros }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useTableroTaller(): UseQueryResult<TableroTaller, ErrorApi> {
  return useQuery<TableroTaller, ErrorApi>({
    queryKey: claves.tableroTaller,
    queryFn: ({ signal }) => api.get<TableroTaller>("/reparaciones/tablero", { signal }),
  });
}

/** Recibir un equipo. `cliente_id` es opcional: la ficha se completa despues. */
/**
 * Recibir el equipo. Devuelve la orden y el cobro del diagnostico, que salen de
 * la misma transaccion: invalida caja porque puede haber entrado plata.
 */
export function useRecibirEquipo(): UseMutationResult<
  ResultadoRecepcion,
  ErrorApi,
  NuevaOrdenReparacion
> {
  const qc = useQueryClient();
  return useMutation<ResultadoRecepcion, ErrorApi, NuevaOrdenReparacion>({
    mutationFn: (datos) => api.post<ResultadoRecepcion>("/reparaciones", datos),
    onSuccess: () =>
      invalidar(qc, [claves.reparaciones, claves.cajas, claves.clientes, claves.productos]),
  });
}

/**
 * Los accesos del equipo (cuenta, contrasena, patron). Se piden solo cuando
 * alguien los quiere ver: `habilitado` arranca en `false` y la ficha lo prende
 * al apretar el boton. No se cachean mas que un momento: son la contrasena de
 * alguien, no un catalogo.
 */
export function useAccesosOt(
  id: number,
  habilitado: boolean,
): UseQueryResult<AccesosEquipo, ErrorApi> {
  return useQuery<AccesosEquipo, ErrorApi>({
    queryKey: claves.accesosOt(id),
    queryFn: ({ signal }) => api.get<AccesosEquipo>(`/reparaciones/${id}/accesos`, { signal }),
    enabled: habilitado,
    staleTime: 0,
    gcTime: 60_000,
  });
}

/** Equipos, transportes y tercerizados ya cargados, para sugerir y precargar. */
export function useSugerenciasOt(): UseQueryResult<SugerenciasOt, ErrorApi> {
  return useQuery<SugerenciasOt, ErrorApi>({
    queryKey: claves.sugerenciasOt,
    queryFn: ({ signal }) => api.get<SugerenciasOt>("/reparaciones/sugerencias", { signal }),
    staleTime: 60_000,
  });
}

/** Que usados del stock estan hoy en el tecnico. Se invalida con todo `reparaciones`. */
export function useUsadosEnTaller(): UseQueryResult<Pagina<UsadoEnTaller>, ErrorApi> {
  return useQuery<Pagina<UsadoEnTaller>, ErrorApi>({
    queryKey: claves.usadosEnTaller,
    queryFn: ({ signal }) => api.get<Pagina<UsadoEnTaller>>("/reparaciones/en-taller", { signal }),
    staleTime: 30_000,
  });
}

/** Los tres montos, cuando el tecnico contesta. La orden pasa a presupuestada. */
export function useCargarCostosOt(): UseMutationResult<
  ResultadoCostosOt,
  ErrorApi,
  { id: number; costos: CostosOt }
> {
  const qc = useQueryClient();
  return useMutation<ResultadoCostosOt, ErrorApi, { id: number; costos: CostosOt }>({
    mutationFn: ({ id, costos }) =>
      api.post<ResultadoCostosOt>(`/reparaciones/${id}/costos`, costos),
    onSuccess: () => invalidar(qc, [claves.reparaciones]),
  });
}

/** Plata que deja el cliente al recibir. Entra a la caja, atada a la orden. */
export function useSenaOt(): UseMutationResult<
  ResultadoSenaOt,
  ErrorApi,
  { id: number; pago: SenaOt }
> {
  const qc = useQueryClient();
  return useMutation<ResultadoSenaOt, ErrorApi, { id: number; pago: SenaOt }>({
    mutationFn: ({ id, pago }) => api.post<ResultadoSenaOt>(`/reparaciones/${id}/sena`, pago),
    onSuccess: () => invalidar(qc, [claves.reparaciones, claves.cajas, claves.clientes]),
  });
}

/**
 * Devolver la seña de una orden anulada. Es el paso explicito: anular no
 * mueve plata sola. Invalida caja y clientes porque sale plata y se cancela el
 * saldo a favor.
 */
export function useDevolverSenaOt(): UseMutationResult<
  ResultadoSenaOt,
  ErrorApi,
  { id: number; pago: DevolverSenaOt }
> {
  const qc = useQueryClient();
  return useMutation<ResultadoSenaOt, ErrorApi, { id: number; pago: DevolverSenaOt }>({
    mutationFn: ({ id, pago }) =>
      api.post<ResultadoSenaOt>(`/reparaciones/${id}/devolver-sena`, pago),
    onSuccess: () => invalidar(qc, [claves.reparaciones, claves.cajas, claves.clientes]),
  });
}

/**
 * Pagarle al tecnico tercerizado.
 *
 * Es el momento en que el costo deja de ser un numero para cotizar y sale plata
 * de la caja. Invalida caja porque es un egreso de verdad.
 */
export function usePagarTecnicoOt(): UseMutationResult<
  ResultadoPagoTecnicoOt,
  ErrorApi,
  { id: number; pago: PagoTecnicoOt }
> {
  const qc = useQueryClient();
  return useMutation<ResultadoPagoTecnicoOt, ErrorApi, { id: number; pago: PagoTecnicoOt }>({
    mutationFn: ({ id, pago }) =>
      api.post<ResultadoPagoTecnicoOt>(`/reparaciones/${id}/pago-tecnico`, pago),
    onSuccess: () => invalidar(qc, [claves.reparaciones, claves.cajas]),
  });
}

/** La liquidacion con el tecnico: N ordenes y N cajas, todo o nada. */
export function usePagarTecnicoLote(): UseMutationResult<
  ResultadoPagoTecnicoLote,
  ErrorApi,
  PagoTecnicoLote
> {
  const qc = useQueryClient();
  return useMutation<ResultadoPagoTecnicoLote, ErrorApi, PagoTecnicoLote>({
    mutationFn: (datos) =>
      api.post<ResultadoPagoTecnicoLote>("/reparaciones/pago-tecnico-lote", datos),
    onSuccess: () => invalidar(qc, [claves.reparaciones, claves.cajas]),
  });
}

/** Entregar: factura, cobra, descuenta las señas y cierra. Toca todo. */
export function useEntregarOt(): UseMutationResult<
  ResultadoEntregaOt,
  ErrorApi,
  { id: number; entrega: EntregaOt }
> {
  const qc = useQueryClient();
  return useMutation<ResultadoEntregaOt, ErrorApi, { id: number; entrega: EntregaOt }>({
    mutationFn: ({ id, entrega }) =>
      api.post<ResultadoEntregaOt>(`/reparaciones/${id}/entregar`, entrega),
    onSuccess: () => {
      invalidar(qc, [claves.reparaciones]);
      invalidarPorComprobante(qc);
    },
  });
}

export function useCambiarEstadoOt(): UseMutationResult<
  unknown,
  ErrorApi,
  { id: number; estado: EstadoOt; datos?: Record<string, unknown> }
> {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ErrorApi,
    { id: number; estado: EstadoOt; datos?: Record<string, unknown> }
  >({
    mutationFn: ({ id, estado, datos }) =>
      api.post(`/reparaciones/${id}/estado`, { estado, datos: datos ?? {} }),
    onSuccess: () => invalidar(qc, [claves.reparaciones]),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Configuracion
// ─────────────────────────────────────────────────────────────────────────────

export function useConfig(): UseQueryResult<RespuestaConfig, ErrorApi> {
  return useQuery<RespuestaConfig, ErrorApi>({
    queryKey: claves.config,
    queryFn: ({ signal }) => api.get<RespuestaConfig>("/config", { signal }),
    staleTime: 60_000,
  });
}

/**
 * Cambiar la configuracion. Es de admin, y con razon: aca vive la cotizacion.
 *
 * Si el cambio la toco, se tira la cache entera. El backend repricia el catalogo
 * con un trigger y las vistas valorizadas recalculan en cada lectura: cualquier
 * importe que el frontend tenga guardado quedo mal en ese mismo segundo. Un
 * refetch de mas cuesta una consulta; un precio viejo en pantalla cuesta una
 * venta mal hecha.
 */
export function useActualizarConfig(): UseMutationResult<RespuestaConfig, ErrorApi, CambiosConfig> {
  const qc = useQueryClient();
  return useMutation<RespuestaConfig, ErrorApi, CambiosConfig>({
    mutationFn: (cambios) => api.patch<RespuestaConfig>("/config", cambios),
    onSuccess: (r, cambios) => {
      qc.setQueryData(claves.config, r);

      const tocoLaCotizacion =
        cambios.cotizacion_modo !== undefined ||
        cambios.cotizacion_manual !== undefined ||
        cambios.spread_pct !== undefined ||
        cambios.ajuste_fijo !== undefined;

      if (tocoLaCotizacion) invalidarPorCotizacion(qc);
      else invalidar(qc, [claves.config]);
    },
  });
}

/**
 * Trae el dolar blue de Buenos Aires y lo deja como cotizacion vigente.
 *
 * El backend consulta DolarHoy y cae a DolarApi con el blue nacional si no
 * responde.
 *
 * Invalida TODO lo que muestre precios, no solo la config: guardar una
 * cotizacion dispara `trg_cotizaciones_reprecia` en la base, que recalcula el
 * catalogo entero. Lo que quede en cache son numeros que ya no existen.
 */
export function useSincronizarCotizacion(): UseMutationResult<RespuestaConfig, ErrorApi, void> {
  const qc = useQueryClient();
  return useMutation<RespuestaConfig, ErrorApi, void>({
    mutationFn: () => api.post<RespuestaConfig>("/config/cotizacion/sincronizar"),
    onSuccess: () => invalidarPorCotizacion(qc),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Usuarios
// ─────────────────────────────────────────────────────────────────────────────

/** Las cuentas que pueden entrar al sistema, activas primero. */
export function useUsuarios(): UseQueryResult<Pagina<FilaUsuario>, ErrorApi> {
  return useQuery<Pagina<FilaUsuario>, ErrorApi>({
    queryKey: claves.usuarios,
    queryFn: ({ signal }) => api.get<Pagina<FilaUsuario>>("/usuarios", { signal }),
  });
}

/** Alta de un usuario. El backend lo crea siempre como administrador. */
export function useCrearUsuario(): UseMutationResult<unknown, ErrorApi, NuevoUsuario> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, NuevoUsuario>({
    mutationFn: (datos) => api.post("/usuarios", datos),
    onSuccess: () => invalidar(qc, [claves.usuarios]),
  });
}

/**
 * Nombre, contraseña, baja o reactivacion. Se refresca tambien la sesion: si el
 * cambio fue el nombre propio, es lo que muestra la barra.
 */
export function useActualizarUsuario(): UseMutationResult<
  unknown,
  ErrorApi,
  { id: string; cambios: CambiosUsuario }
> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, { id: string; cambios: CambiosUsuario }>({
    mutationFn: ({ id, cambios }) => api.patch(`/usuarios/${id}`, cambios),
    onSuccess: () => invalidar(qc, [claves.usuarios, claves.sesion]),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Socios
// ─────────────────────────────────────────────────────────────────────────────

/** Los socios activos. Cambian una vez cada muchos años: no hace falta refrescarlos. */
export function useSocios(): UseQueryResult<Pagina<Socio>, ErrorApi> {
  return useQuery<Pagina<Socio>, ErrorApi>({
    queryKey: claves.socios,
    queryFn: ({ signal }) => api.get<Pagina<Socio>>("/socios", { signal }),
    staleTime: Infinity,
  });
}

/** Todo lo que sacaron y pusieron los socios en el periodo, mas los viejos sin dueño. */
export function useMovimientosSocios(
  rango: RangoDeQuery = {},
): UseQueryResult<PaginaMovimientosSocios, ErrorApi> {
  return useQuery<PaginaMovimientosSocios, ErrorApi>({
    queryKey: claves.movimientosSocios(rango),
    queryFn: ({ signal }) =>
      api.get<PaginaMovimientosSocios>("/socios/movimientos", { params: { ...rango }, signal }),
    placeholderData: keepPreviousData,
  });
}

/** Ponerle dueño a un retiro o aporte cargado antes de que existieran los socios. */
export function useAsignarSocio(): UseMutationResult<
  unknown,
  ErrorApi,
  { movimientoId: number; socioId: number }
> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, { movimientoId: number; socioId: number }>({
    mutationFn: ({ movimientoId, socioId }) =>
      api.patch(`/cajas/movimientos/${movimientoId}/socio`, { socio_id: socioId }),
    onSuccess: () => invalidar(qc, [claves.socios, claves.cajas]),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Encargos (0111)
// ─────────────────────────────────────────────────────────────────────────────

/** Lo vendido a pedido, por estado o de una venta. */
export function useEncargos(
  filtros: { estado?: EstadoEncargo; comprobante_id?: number } = {},
): UseQueryResult<Pagina<Encargo>, ErrorApi> {
  return useQuery<Pagina<Encargo>, ErrorApi>({
    queryKey: claves.listaEncargos(filtros),
    queryFn: ({ signal }) =>
      api.get<Pagina<Encargo>>("/encargos", { params: { ...filtros }, signal }),
  });
}

/** Llegó sin factura: se da por recibido con el costo que se cargue. */
export function useRecibirEncargo(): UseMutationResult<
  unknown,
  ErrorApi,
  { id: number; costo: number; moneda: Moneda }
> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, { id: number; costo: number; moneda: Moneda }>({
    mutationFn: ({ id, costo, moneda }) => api.post(`/encargos/${id}/recibir`, { costo, moneda }),
    // El costo real cambia la ganancia de la venta.
    onSuccess: () => invalidar(qc, [claves.encargos, claves.ventas, claves.stock]),
  });
}

/** El cliente se lo llevó. */
export function useEntregarEncargo(): UseMutationResult<unknown, ErrorApi, number> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, number>({
    mutationFn: (id) => api.post(`/encargos/${id}/entregar`, {}),
    onSuccess: () => invalidar(qc, [claves.encargos, claves.ventas]),
  });
}
